<script lang="ts">
	import { serviceContext } from './ServiceContext';
	import { onDestroy, tick } from 'svelte';
	import Button from './shadcn/components/ui/button/button.svelte';
	import { Spinner } from './shadcn/components/ui/spinner';
	import * as InputOTP from './shadcn/components/ui/input-otp';
	import { SiGoogle } from '@icons-pack/svelte-simple-icons';
	import { type AuthResult } from './AuthService.svelte';
	import { fetchRetry } from './utils';
	import { Mail } from '@lucide/svelte';
	import { Input } from './shadcn/components/ui/input';
	import { Separator } from './shadcn/components/ui/separator';
	import z from 'zod';
	import { logger } from '@sentry/sveltekit';

	// @wc-ignore
	const UIState = {
		Initial: 'Initial',
		EnterOtp: 'EnterOtp',
		VerifyingOtp: 'VerifyingOtp',
		Loading: 'Loading',
		Onboarding: 'Onboarding',
		Done: 'Done',
		Error: 'Error'
	} as const;
	type UIState = (typeof UIState)[keyof typeof UIState];

	let {
		onSuccess = async () => {},
		afterSuccess = async () => {},
		onFailure = async () => {},
		onBegin = async () => {},
		hideHeading = false,
		// @wc-include
		heading = 'Sign in',
		// @wc-include
		loadingHeading = 'Signing in...',
		providers = ['firebase', 'email-otp']
	}: {
		onSuccess?: (result: AuthResult) => Promise<void>;
		afterSuccess?: (result: AuthResult) => Promise<void>;
		onFailure?: (error: unknown) => Promise<void>;
		onBegin?: () => Promise<void>;
		hideHeading?: boolean;
		heading?: string;
		loadingHeading?: string;
		providers?: Array<'firebase' | 'email-otp'>;
	} = $props();

	let signInState = $state<UIState>(UIState.Initial);
	let loginResult: AuthResult;
	let onBoardingStarted = $state(false);
	let componentUnmounted = false;
	let email = $state('');
	let otp = $state('');
	let otpError = $state(false);

	function abortIfUnmounted() {
		if (componentUnmounted) {
			throw new Error('Signin aborted due to component unmount');
		}
	}

	onDestroy(async () => {
		componentUnmounted = true;
		await tick();

		if (signInState == UIState.Initial) {
			return;
		}
		if (signInState != UIState.Done && signInState != UIState.Error) {
			await onFailure('Component unmounted');
		}
	});

	async function signInWithSocialFn(signInFn: () => Promise<AuthResult>) {
		abortIfUnmounted();
		await onBegin();
		signInState = UIState.Loading;
		await tick();
		try {
			loginResult = await signInFn();
			await tick();
			abortIfUnmounted();
			if (!(await serviceContext.userService.isUserOnboardingComplete())) {
				abortIfUnmounted();
				signInState = UIState.Onboarding;
			} else {
				abortIfUnmounted();
				await onSuccess(loginResult);
				signInState = UIState.Done;
				await tick();
				await afterSuccess(loginResult);
			}
		} catch (error) {
			logger.warn('Unable to sign in', { error, function: 'signInWithSocialFn' });
			abortIfUnmounted();
			await onFailure(error);
			signInState = UIState.Error;
		}
	}

	async function signInWithGoogle() {
		return await signInWithSocialFn(serviceContext.authService.signInWithGoogle);
	}

	async function requestEmailOtp() {
		abortIfUnmounted();
		if (!z.email().safeParse(email).success) {
			return;
		}
		signInState = UIState.EnterOtp;
		await serviceContext.authService.requestEmailOtp(email);
	}

	async function signInWithEmailOtp() {
		abortIfUnmounted();
		await onBegin();
		signInState = UIState.VerifyingOtp;
		await tick();
		try {
			loginResult = await serviceContext.authService.signInWithEmailOtp(email, otp);
			await tick();
			abortIfUnmounted();
			if (!(await serviceContext.userService.isUserOnboardingComplete())) {
				abortIfUnmounted();
				signInState = UIState.Onboarding;
			} else {
				abortIfUnmounted();
				await onSuccess(loginResult);
				signInState = UIState.Done;
				await tick();
				await afterSuccess(loginResult);
			}
		} catch (error) {
			logger.warn('Unable to sign in', { error, function: 'signInWithEmailOtp' });
			abortIfUnmounted();
			otpError = true;
			otp = '';
			await onFailure(error);
			signInState = UIState.EnterOtp;
		}
	}

	function cancelEmailOtp() {
		otpError = false;
		otp = '';
		email = '';
		signInState = UIState.Initial;
	}

	async function setMarketing(enable: boolean) {
		await fetchRetry.json(
			'/api/user/toggle-marketing',
			{ enable },
			{
				method: 'post'
			},
			{ retries: 3 }
		);
	}

	async function setUserOnboardingCompleted() {
		await fetchRetry.json(
			'/api/user/complete-onboarding',
			{ level: 1 },
			{
				method: 'post'
			},
			{ retries: 3 }
		);
	}

	async function completeOnboarding(optIn: boolean) {
		onBoardingStarted = true;

		if (signInState != UIState.Onboarding) {
			abortIfUnmounted();
			onFailure('invalid onboarding state');
			signInState = UIState.Error;
			return;
		}

		const userId = serviceContext.authService.getUser()?.uid;
		if (!userId) {
			abortIfUnmounted();
			await onFailure('invalid onboarding state: user is not sign in');
			signInState = UIState.Error;
			return;
		}
		try {
			abortIfUnmounted();
			await setMarketing(optIn).catch((error) =>
				logger.error('Unable to set marketing value', { error })
			);
			await setUserOnboardingCompleted();
			abortIfUnmounted();
			await onSuccess(loginResult);
			signInState = UIState.Done;
			await tick();
			await afterSuccess(loginResult);
		} catch (error) {
			abortIfUnmounted();
			await onFailure(error);
			signInState = UIState.Error;
			logger.warn('Unable to sign in', { error, function: 'completeOnboarding' });
		}
	}
</script>

<div class="flex h-full w-full items-center justify-center">
	<div class="xs:w-80 preset-filled card flex w-full max-w-80 flex-col items-center gap-4 p-4">
		{#if signInState == UIState.Initial}
			{#if !hideHeading}
				<p class="h3 text-center">{heading}</p>
			{/if}
			<div class="flex w-full max-w-60 flex-col gap-2">
				{#if providers.includes('firebase')}
					<Button
						variant="tonal"
						onclick={signInWithGoogle}
						class="justify-start"
						disabled={!serviceContext.authService.firebaseAuthReady()}
					>
						<SiGoogle />
						Sign in with Google
						{#if !serviceContext.authService.firebaseAuthReady()}
							<Spinner />
						{/if}
					</Button>
				{/if}
				{#if providers.includes('email-otp')}
					<div class="flex items-center gap-2">
						<Separator class="shrink" />
						OR
						<Separator class="shrink" />
					</div>
					<form
						class="flex flex-col gap-2"
						autocomplete="on"
						onsubmit={(e) => {
							requestEmailOtp();
							e.preventDefault();
							return false;
						}}
					>
						<Input
							variant="tonal"
							type="email"
							placeholder="Email"
							bind:value={email}
							autocomplete="email"
							name="email"
							required
						/>
						<Button type="submit" variant="tonal" class="w-fit place-self-center"
							><Mail />Receive OTP</Button
						>
					</form>
				{/if}
			</div>
			<p class="text-xs">
				By signing in you agree to our <a class="anchor" href="/privacy">Terms of Service</a>
				and
				<a class="anchor" href="/privacy">Privacy Policy</a>.
			</p>
		{:else if signInState == UIState.EnterOtp || signInState == UIState.VerifyingOtp}
			{#if !hideHeading}
				<p class="h3 text-center">{loadingHeading}</p>
			{/if}
			<div class="flex flex-col gap-2">
				<div class="flex flex-col">
					<p class="font-bold">Email:</p>
					<p>{email}</p>
				</div>
				<div class="flex flex-col">
					<p class="font-bold">Enter OTP:</p>
					<InputOTP.Root
						maxlength={6}
						bind:value={otp}
						class=""
						autofocus
						name="one-time-code"
						autocomplete="one-time-code"
						onValueChange={() => {
							if (otp.length == 6) {
								signInWithEmailOtp();
							}
						}}
						disabled={signInState == UIState.VerifyingOtp}
					>
						{#snippet children({ cells })}
							<InputOTP.Group>
								{#each cells as cell (cell)}
									<InputOTP.Slot {cell} />
								{/each}
							</InputOTP.Group>
						{/snippet}
					</InputOTP.Root>
					{#if otpError}
						<p class="text-destructive">Provided OTP invalid</p>
					{/if}
				</div>
				<div class="flex justify-between gap-2">
					<Button
						variant="link"
						class="w-fit px-0 text-left"
						onclick={requestEmailOtp}
						disabled={signInState == UIState.VerifyingOtp}>Resend OTP</Button
					>
					<Button
						variant="tonal"
						class="w-fit"
						onclick={signInWithEmailOtp}
						disabled={signInState == UIState.VerifyingOtp && otp.length != 6}
					>
						{#if signInState == UIState.EnterOtp}
							Submit
						{:else}
							<Spinner />
						{/if}
					</Button>
				</div>
				<Button
					variant="link"
					class="text-muted w-fit px-0 text-left"
					onclick={cancelEmailOtp}
					disabled={signInState == UIState.VerifyingOtp}>Cancel</Button
				>
			</div>
		{:else if signInState == UIState.Loading}
			{#if !hideHeading}
				<p class="h3 text-center">{loadingHeading}</p>
			{/if}
			<Spinner class="size-8" />
		{:else if signInState == UIState.Onboarding}
			<div class="flex w-full flex-col items-center gap-2">
				<p class="h3 text-center">Want Emails?</p>
				<p class="text-center">
					Receive email updates about the plugin release and about upcoming changes.
				</p>
			</div>
			<div class="flex w-full grow justify-center gap-2">
				{#if !onBoardingStarted}
					<Button
						variant="tonal"
						size="lg"
						class="btn grow font-bold"
						onclick={() => completeOnboarding(true)}>Yes</Button
					>
					<Button
						variant="tonal"
						size="lg"
						class="btn grow"
						onclick={() => completeOnboarding(false)}>No</Button
					>
				{:else}
					<Spinner class="size-8" />
				{/if}
			</div>
		{:else if signInState == UIState.Done}
			{#if !hideHeading}
				<p class="h3 text-center">{heading}</p>
			{/if}
			<p class="text-center font-bold">
				Sign in successful. <br />You can close this page now.
			</p>
		{:else if signInState == UIState.Error}
			{#if !hideHeading}
				<p class="h3 text-center">{heading}</p>
			{/if}
			<p class="text-error-500 text-center font-bold">Error during sign in. Please try again.</p>
		{/if}
	</div>
</div>
